import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Calendar,
  Download,
  Users,
  List,
  LayoutGrid,
  X,
  Eye,
} from 'lucide-react';

const Reports = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isManager } = useAuthStore();
  const [reportData, setReportData] = useState(null);
  const [areaData, setAreaData] = useState([]);
  const [teamEntries, setTeamEntries] = useState([]); // Initialized as empty array to prevent .map crashes
  const [employeeTotals, setEmployeeTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [teamSort, setTeamSort] = useState('recent');
  const [employeeSort, setEmployeeSort] = useState('active');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  const [hoveredNote, setHoveredNote] = useState(null);
  const [blurbPos, setBlurbPos] = useState({ x: 0, y: 0 });

  // Pagination states
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPagination, setSummaryPagination] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });

  const [teamPage, setTeamPage] = useState(1);
  const [teamPagination, setTeamPagination] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });

  const [myEntries, setMyEntries] = useState([]);
  const [myPage, setMyPage] = useState(1);
  const [myPagination, setMyPagination] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });

  const [employeePage, setEmployeePage] = useState(1);
  const employeePageSize = 10;

  // View modes
  const [summaryViewMode, setSummaryViewMode] = useState('list'); // 'list' or 'block'
  const [teamViewMode, setTeamViewMode] = useState('list'); // 'list' or 'block'
  const [myViewMode, setMyViewMode] = useState('list');

  // Detailed modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Reset pagination when dateRange or activeTab changes
  useEffect(() => {
    setSummaryPage(1);
    setTeamPage(1);
    setMyPage(1);
    setEmployeePage(1);
  }, [dateRange, activeTab]);

  const fetchSummaryReport = async () => {
    setLoading(true);
    try {
      const summaryRes = await reportsAPI.getSummary({ ...dateRange, page: summaryPage, size: 10 });
      const areaRes = await reportsAPI.getByReportingArea(dateRange);
      
      setReportData(summaryRes?.data?.data || null);
      setAreaData(areaRes?.data?.data || []);
      if (summaryRes?.data?.pagination) {
        setSummaryPagination(summaryRes.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching summary reports:', error);
      setReportData(null);
      setAreaData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getDaily({ ...dateRange, page: teamPage, size: 10 });
      if (res?.data) {
        let allEntries = [];
        const dailySummary = res.data.data?.daily_summary || [];
        dailySummary.forEach(day => {
          if (day.entries) {
            const entriesWithDate = day.entries.map(e => ({ ...e, work_date: day.date }));
            allEntries = [...allEntries, ...entriesWithDate];
          }
        });
        
        // Sort by id descending (proxy for newest first)
        allEntries.sort((a, b) => b.id - a.id);
        setTeamEntries(allEntries);
        if (res.data.pagination) {
          setTeamPagination(res.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching detailed activity report:', error);
      setTeamEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReport = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await reportsAPI.getDaily({ ...dateRange, user_id: user.id, page: myPage, size: 10 });
      if (res?.data) {
        let allEntries = [];
        const dailySummary = res.data.data?.daily_summary || [];
        dailySummary.forEach(day => {
          if (day.entries) {
            const entriesWithDate = day.entries.map(e => ({ ...e, work_date: day.date }));
            allEntries = [...allEntries, ...entriesWithDate];
          }
        });
        
        allEntries.sort((a, b) => b.id - a.id);
        setMyEntries(allEntries);
        if (res.data.pagination) {
          setMyPagination(res.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching my detailed report:', error);
      setMyEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeesReport = async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.getDaily({ ...dateRange, page: 1, size: 200 });
      if (res?.data) {
        let allEntries = [];
        const dailySummary = res.data.data?.daily_summary || [];
        dailySummary.forEach(day => {
          if (day.entries) {
            const entriesWithDate = day.entries.map(e => ({ ...e, work_date: day.date }));
            allEntries = [...allEntries, ...entriesWithDate];
          }
        });
        
        const empMap = {};
        allEntries.forEach(entry => {
          if (!empMap[entry.user_name]) {
            empMap[entry.user_name] = { name: entry.user_name, total: 0, projects: new Set() };
          }
          empMap[entry.user_name].total += entry.hours;
          empMap[entry.user_name].projects.add(entry.project_name);
        });
        setEmployeeTotals(Object.values(empMap).sort((a, b) => b.total - a.total));
      }
    } catch (error) {
      console.error('Error fetching employee totals report:', error);
      setEmployeeTotals([]);
    } finally {
      setLoading(false);
    }
  };

  // Tab-specific lazy fetching
  useEffect(() => {
    if (activeTab === 'summary') {
      fetchSummaryReport();
    } else if (activeTab === 'team') {
      fetchTeamReport();
    } else if (activeTab === 'my-activity') {
      fetchMyReport();
    } else if (activeTab === 'employees') {
      fetchEmployeesReport();
    }
  }, [activeTab, dateRange, summaryPage, teamPage, myPage]);

  const renderReportsPagination = (currentPage, totalPages, totalRecords, size, onPageChange) => {
    if (totalPages <= 1) return null;

    const startRecord = (currentPage - 1) * size + 1;
    const endRecord = Math.min(currentPage * size, totalRecords);

    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="premium-pagination">
        <div className="premium-pagination-info">
          Showing <span style={{ fontWeight: 600 }}>{startRecord}</span> to{' '}
          <span style={{ fontWeight: 600 }}>{endRecord}</span> of{' '}
          <span style={{ fontWeight: 600 }}>{totalRecords}</span> entries
        </div>
        <div className="premium-pagination-controls">
          <button
            className="premium-pagination-btn"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            title="First Page"
          >
            &lt;&lt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            title="Previous Page"
          >
            &lt;
          </button>
          
          {startPage > 1 && (
            <>
              <button className="premium-pagination-btn" onClick={() => onPageChange(1)}>1</button>
              {startPage > 2 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
            </>
          )}

          {pageNumbers.map(num => (
            <button
              key={num}
              className={`premium-pagination-btn ${currentPage === num ? 'active' : ''}`}
              onClick={() => onPageChange(num)}
            >
              {num}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
              <button className="premium-pagination-btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
            </>
          )}

          <button
            className="premium-pagination-btn"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            title="Next Page"
          >
            &gt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            title="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    );
  };

  const renderSummaryBlockView = () => {
    return (
      <div className="block-grid">
        {(reportData?.project_summary || []).map((project, idx) => (
          <div key={project.project_id || project.group_key || idx} className="block-card">
            <div className="block-card-header">
              <div>
                <div className="block-card-title">{project.project_name}</div>
                <div className="block-card-subtitle">{project.client}</div>
              </div>
            </div>
            <div className="block-card-body">
              <div className="block-card-field">
                <span className="block-card-label">Reporting Area:</span>
                <span className="block-card-value">{project.reporting_area || 'N/A'}</span>
              </div>
              <div className="block-card-field">
                <span className="block-card-label">Total Hours:</span>
                <span className="block-card-value font-bold text-teal-600" style={{ fontSize: '15px' }}>
                  {parseFloat(project.total_hours).toFixed(1)}h
                </span>
              </div>
              {project.user_hours && project.user_hours.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div className="block-card-label" style={{ fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px', fontWeight: 600 }}>Contributions</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                    {project.user_hours.map((uh, uidx) => {
                      const percentage = project.total_hours > 0 ? ((parseFloat(uh.hours) / parseFloat(project.total_hours)) * 100).toFixed(0) : 0;
                      return (
                        <div key={uidx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <span>{uh.user_name}</span>
                          <span className="font-medium">{uh.hours}h ({percentage}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDetailedReportSection = (
    entries, 
    page, 
    pagination, 
    setPage, 
    viewMode, 
    setViewMode, 
    isTeamReport
  ) => {
    const renderBlock = () => {
      const sortedEntries = [...entries].sort((a, b) => {
        if (teamSort === 'alphabetical') {
          const nameA = a.user_name || '';
          const nameB = b.user_name || '';
          return nameA.localeCompare(nameB);
        }
        if (teamSort === 'hours') {
          return b.hours - a.hours;
        }
        return b.id - a.id;
      });

      return (
        <div className="block-grid">
          {sortedEntries.map((entry) => (
            <div key={entry.id} className="block-card">
              <div className="block-card-header">
                <div>
                  {isTeamReport && (isAdmin() || isManager()) && (
                    <div className="block-card-title">{entry.user_name}</div>
                  )}
                  <div className="block-card-subtitle">
                    {new Date(entry.work_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div>
                  <span className={`status-label ${entry.status.toLowerCase()}`}>{entry.status}</span>
                </div>
              </div>
              <div className="block-card-body">
                <div className="block-card-field">
                  <span className="block-card-label">Project:</span>
                  <span className="block-card-value">{entry.project_name}</span>
                </div>
                {entry.activity_type && (
                  <div className="block-card-field">
                    <span className="block-card-label">Activity:</span>
                    <span className="block-card-value" style={{ textTransform: 'capitalize' }}>
                      {entry.activity_type.replace('_', ' ')}
                    </span>
                  </div>
                )}
                <div className="block-card-field">
                  <span className="block-card-label">Hours:</span>
                  <span className="block-card-value font-bold text-teal-600" style={{ fontSize: '15px' }}>
                    {parseFloat(entry.hours).toFixed(1)}h
                  </span>
                </div>
                {entry.approved_by_name && (
                  <div className="block-card-field">
                    <span className="block-card-label">Approved By:</span>
                    <span className="block-card-value italic" style={{ fontSize: '12px' }}>{entry.approved_by_name}</span>
                  </div>
                )}
                <div 
                  className="block-card-notes"
                  onClick={() => {
                    setSelectedEntry(entry);
                    setShowDetailModal(true);
                  }}
                  title="Click to view full notes"
                >
                  {entry.notes || <span className="italic text-slate-300">No notes provided</span>}
                </div>
                {entry.status === 'REJECTED' && entry.rejection_reason && (
                  <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '6px', borderLeft: '3px solid var(--error)' }}>
                    <div className="text-[10px] text-red-600 font-bold uppercase">Rejection Reason:</div>
                    <div className="text-xs text-red-800">{entry.rejection_reason}</div>
                  </div>
                )}
              </div>
              <div className="block-card-footer">
                {entry.status === 'REJECTED' && entry.user_id === user?.id && (
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate(`/timesheets?date=${entry.work_date}`)}
                  >
                    Fix & Resubmit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    };

    return (
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">
            {isTeamReport ? 'Detailed Team Activity' : 'My Detailed Activity'}
          </h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span className="text-xs text-secondary font-medium">Sort:</span>
            <select 
              className="form-input" 
              style={{ padding: '4px 8px', fontSize: '13px', width: 'auto', marginBottom: 0 }}
              value={teamSort}
              onChange={(e) => setTeamSort(e.target.value)}
            >
              <option value="recent">Most Recent</option>
              <option value="alphabetical">Alphabetical (A-Z)</option>
              <option value="hours">Hours (High to Low)</option>
            </select>
            
            <div className="view-toggle-container">
              <button 
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={16} />
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'block' ? 'active' : ''}`}
                onClick={() => setViewMode('block')}
                title="Block/Grid View"
              >
                <LayoutGrid size={16} />
              </button>
            </div>
          </div>
        </div>

        {renderReportsPagination(page, pagination.total_pages, pagination.total_records, 10, setPage)}

        {viewMode === 'list' ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {isTeamReport && (isAdmin() || isManager()) && <th>Employee</th>}
                  <th>Project / Activity</th>
                  <th>Date</th>
                  <th className="text-center">Hours</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length > 0 ? (
                  [...entries].sort((a, b) => {
                    if (teamSort === 'alphabetical') {
                      const nameA = a.user_name || '';
                      const nameB = b.user_name || '';
                      return nameA.localeCompare(nameB);
                    }
                    if (teamSort === 'hours') {
                      return b.hours - a.hours;
                    }
                    return b.id - a.id;
                  }).map((entry) => (
                    <tr key={entry.id}>
                      {isTeamReport && (isAdmin() || isManager()) && (
                        <td className="font-medium text-slate-900">{entry.user_name}</td>
                      )}
                      <td>
                        <div className="font-medium">{entry.project_name}</div>
                        {entry.activity_type && (
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">{entry.activity_type.replace('_', ' ')}</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap text-slate-600">
                        {new Date(entry.work_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="text-center font-bold text-slate-700">{parseFloat(entry.hours).toFixed(1)}h</td>
                      <td>
                        <div className="flex flex-col gap-1">
                          <span className={`status-label ${entry.status.toLowerCase()}`}>
                            {entry.status}
                          </span>
                          {entry.approved_by_name && (
                            <span className="text-[10px] text-slate-500 italic">
                              by {entry.approved_by_name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td 
                        style={{ maxWidth: '300px', position: 'relative', cursor: 'pointer' }}
                        onClick={() => { setSelectedEntry(entry); setShowDetailModal(true); }}
                        onMouseEnter={(e) => {
                          if (entry.notes) {
                            setHoveredNote(entry.id);
                            setBlurbPos({ x: e.clientX, y: e.clientY });
                          }
                        }}
                        onMouseLeave={() => setHoveredNote(null)}
                        title="Click to view details"
                      >
                        <div className="text-sm text-slate-600 leading-relaxed truncate" style={{ maxWidth: '250px' }}>
                          {entry.notes || <span className="text-slate-300 italic">No notes</span>}
                        </div>
                        {hoveredNote === entry.id && entry.notes && (
                          <div 
                            style={{ 
                              position: 'fixed', 
                              left: blurbPos.x + 10, 
                              top: blurbPos.y + 10, 
                              background: 'white', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px', 
                              boxShadow: 'var(--shadow-lg)', 
                              padding: '12px', 
                              zIndex: 9999, 
                              maxWidth: '350px',
                              fontSize: '13px',
                              color: 'var(--text-primary)',
                              pointerEvents: 'none',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase' }}>Full Activity Notes</div>
                            {entry.notes}
                          </div>
                        )}
                        {entry.status === 'REJECTED' && entry.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-50 border-l-2 border-red-400 rounded-r shadow-sm">
                            <p className="text-[10px] text-red-600 font-bold uppercase tracking-tight">Rejection Reason:</p>
                            <p className="text-xs text-red-800">{entry.rejection_reason}</p>
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        {entry.status === 'REJECTED' && entry.user_id === user?.id && (
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/timesheets?date=${entry.work_date}`)}
                            style={{ fontSize: '11px', padding: '4px 10px' }}
                          >
                            Fix & Resubmit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={isTeamReport && (isAdmin() || isManager()) ? 7 : 6} className="text-center py-8 text-secondary">
                      No activity found for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '0 24px 24px' }}>
            {renderBlock()}
          </div>
        )}

        {renderReportsPagination(page, pagination.total_pages, pagination.total_records, 10, setPage)}
      </div>
    );
  };

  const handleDateChange = (field, value) => {
    setDateRange({ ...dateRange, [field]: value });
  };

  const exportToCSV = () => {
    if (!reportData) return;
    let csv = 'Project,Client,Reporting Area,Total Hours\n';
    reportData.project_summary.forEach(project => {
      csv += `"${project.project_name}","${project.client}","${project.reporting_area || 'N/A'}",${project.total_hours}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-${dateRange.start_date}-to-${dateRange.end_date}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>Reports</h2>
          <p>View time tracking analytics and summaries</p>
        </div>
        <div className="header-actions">
          <div className="flex gap-2">
            <input
              type="date"
              className="form-input"
              style={{ width: 'auto' }}
              value={dateRange.start_date}
              onChange={(e) => handleDateChange('start_date', e.target.value)}
            />
            <span className="text-secondary" style={{ alignSelf: 'center' }}>to</span>
            <input
              type="date"
              className="form-input"
              style={{ width: 'auto' }}
              value={dateRange.end_date}
              onChange={(e) => handleDateChange('end_date', e.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </header>

      <div className="page-content">

      <div className="tab-group">
        <button
          onClick={() => setActiveTab('summary')}
          className={`tab-item ${activeTab === 'summary' ? 'active' : ''}`}
        >
          <TrendingUp size={16} />
          Summary Analytics
        </button>
        {(isAdmin() || isManager()) && (
          <button
            onClick={() => setActiveTab('my-activity')}
            className={`tab-item ${activeTab === 'my-activity' ? 'active' : ''}`}
          >
            <List size={16} />
            My Detailed Activity
          </button>
        )}
        <button
          onClick={() => setActiveTab('team')}
          className={`tab-item ${activeTab === 'team' ? 'active' : ''}`}
        >
          <List size={16} />
          {isAdmin() || isManager() ? 'Detailed Team Activity' : 'My Detailed Activity'}
        </button>
        {(isAdmin() || isManager()) && (
          <button
            onClick={() => setActiveTab('employees')}
            className={`tab-item ${activeTab === 'employees' ? 'active' : ''}`}
          >
            <Users size={16} />
            Team Member Summary
          </button>
        )}
      </div>

      <div className="page-content">
        {activeTab === 'summary' && (
          <div className="reports-tab-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon-wrapper primary">
                  <TrendingUp size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">Total Hours Logged</span>
                  <h3 className="stat-value">{reportData?.total_hours || 0}h</h3>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon-wrapper secondary">
                  <BarChart3 size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">Active Projects</span>
                  <h3 className="stat-value">{reportData?.total_projects || 0}</h3>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon-wrapper info">
                  <PieChart size={24} />
                </div>
                <div className="stat-info">
                  <span className="stat-label">Reporting Areas</span>
                  <h3 className="stat-value">{areaData?.length || 0}</h3>
                </div>
              </div>
            </div>

            <div className="card report-section">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title">Project Participation</h3>
                <div className="view-toggle-container">
                  <button 
                    className={`view-toggle-btn ${summaryViewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setSummaryViewMode('list')}
                    title="List View"
                  >
                    <List size={16} />
                  </button>
                  <button 
                    className={`view-toggle-btn ${summaryViewMode === 'block' ? 'active' : ''}`}
                    onClick={() => setSummaryViewMode('block')}
                    title="Block/Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                </div>
              </div>
              
              {renderReportsPagination(summaryPage, summaryPagination.total_pages, summaryPagination.total_records, 10, setSummaryPage)}

              {summaryViewMode === 'list' ? (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Client</th>
                        <th>Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData?.project_summary || []).map((project, idx) => (
                        <tr key={project.project_id || idx}>
                          <td><b>{project.project_name}</b></td>
                          <td>{project.client}</td>
                          <td><span className="font-bold">{parseFloat(project.total_hours).toFixed(1)}h</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '0 24px 24px' }}>
                  {renderSummaryBlockView()}
                </div>
              )}

              {renderReportsPagination(summaryPage, summaryPagination.total_pages, summaryPagination.total_records, 10, setSummaryPage)}
            </div>
          </div>
        )}

        {activeTab === 'my-activity' && (
          renderDetailedReportSection(
            myEntries,
            myPage,
            myPagination,
            setMyPage,
            myViewMode,
            setMyViewMode,
            false
          )
        )}

        {activeTab === 'team' && (
          renderDetailedReportSection(
            teamEntries,
            teamPage,
            teamPagination,
            setTeamPage,
            teamViewMode,
            setTeamViewMode,
            true
          )
        )}

        {activeTab === 'employees' && (() => {
          const sortedTotals = [...employeeTotals].sort((a, b) => {
            if (employeeSort === 'alphabetical') return a.name.localeCompare(b.name);
            return b.total - a.total;
          });
          const totalEmployeePages = Math.ceil(sortedTotals.length / employeePageSize) || 1;
          const paginatedEmployees = sortedTotals.slice((employeePage - 1) * employeePageSize, employeePage * employeePageSize);

          return (
            <div className="card report-section">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="card-title">Team Member Performance Summary</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span className="text-xs text-secondary font-medium">Sort By:</span>
                  <select 
                    className="form-input" 
                    style={{ padding: '4px 8px', fontSize: '13px', width: 'auto', marginBottom: 0 }}
                    value={employeeSort}
                    onChange={(e) => setEmployeeSort(e.target.value)}
                  >
                    <option value="active">Most Active (Hours)</option>
                    <option value="alphabetical">Alphabetical (A-Z)</option>
                  </select>
                </div>
              </div>

              {renderReportsPagination(employeePage, totalEmployeePages, sortedTotals.length, employeePageSize, setEmployeePage)}

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Projects Involved</th>
                      <th className="text-right">Total Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEmployees.length > 0 ? (
                      paginatedEmployees.map((emp, idx) => (
                        <tr key={idx}>
                          <td><b>{emp.name}</b></td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {Array.from(emp.projects).map((p, pidx) => (
                                <span key={pidx} className="text-xs px-2 py-0.5 bg-slate-100 rounded-full border border-slate-200">
                                  {p}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="text-right font-bold text-lg text-primary">{emp.total.toFixed(1)}h</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-8 text-secondary">
                          No team data found for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {renderReportsPagination(employeePage, totalEmployeePages, sortedTotals.length, employeePageSize, setEmployeePage)}
            </div>
          );
        })()}
      </div>

      {/* Detailed Entry Modal */}
      {showDetailModal && selectedEntry && (
        <div className="detail-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="detail-modal-card" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div className="detail-modal-title">Activity Details</div>
              <button className="btn-icon" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="detail-modal-body">
              {(isAdmin() || isManager()) && (
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Employee</div>
                  <div className="detail-modal-value font-semibold">{selectedEntry.user_name}</div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Date</div>
                  <div className="detail-modal-value">
                    {new Date(selectedEntry.work_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Hours</div>
                  <div className="detail-modal-value font-bold text-teal-600" style={{ fontSize: '16px' }}>{parseFloat(selectedEntry.hours).toFixed(1)}h</div>
                </div>
              </div>
              <div className="detail-modal-field">
                <div className="detail-modal-label">Project</div>
                <div className="detail-modal-value font-semibold">{selectedEntry.project_name}</div>
              </div>
              {selectedEntry.activity_type && (
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Activity Type</div>
                  <div className="detail-modal-value" style={{ textTransform: 'capitalize' }}>
                    {selectedEntry.activity_type.replace('_', ' ')}
                  </div>
                </div>
              )}
              <div className="detail-modal-field">
                <div className="detail-modal-label">Status</div>
                <div className="detail-modal-value">
                  <span className={`status-label ${selectedEntry.status.toLowerCase()}`}>{selectedEntry.status}</span>
                  {selectedEntry.approved_by_name && (
                    <span className="text-xs text-slate-500 italic ml-2">
                      by {selectedEntry.approved_by_name}
                    </span>
                  )}
                </div>
              </div>
              {selectedEntry.status === 'REJECTED' && selectedEntry.rejection_reason && (
                <div className="detail-modal-field">
                  <div className="detail-modal-label" style={{ color: 'var(--error)' }}>Rejection Reason</div>
                  <div className="detail-modal-value" style={{ padding: '8px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '6px', borderLeft: '3px solid var(--error)' }}>
                    {selectedEntry.rejection_reason}
                  </div>
                </div>
              )}
              <div className="detail-modal-field">
                <div className="detail-modal-label">Notes</div>
                <div className="detail-modal-notes">
                  {selectedEntry.notes || <span className="italic text-slate-400">No notes provided</span>}
                </div>
              </div>
            </div>
            <div className="detail-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
};

const Clock = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export default Reports;
